import { useCallback, useMemo } from 'react';
import { ActivityLog, getActivityLogDurationMs } from '../../../Model/Activity/ActivityLog';
import { Activity } from '../../../Model/Activity/Activity';
import { BaseTrackingKey } from '../../../Model/ProgramPortfolio/TrackingKeys';

export type ValidationLimits = {
  min?: number;
  max?: (durationHours: number) => number;
  warningMin?: number;
  warningMax?: (durationHours: number) => number;
};

export const VALIDATION_LIMITS: { [key in BaseTrackingKey]?: ValidationLimits } = {
  CALORIES: { min: 0, max: (h) => Math.max(1000, h * 2000), warningMax: (h) => Math.max(500, h * 1000) },
  DISTANCE: { min: 0, max: (h) => Math.max(50, h * 100), warningMax: (h) => Math.max(20, h * 40) },
  HEART_RATE: { min: 40, max: () => 220, warningMin: 50, warningMax: () => 200 },
  LEVEL: { min: 1, max: () => 100 },
  PACE: { min: 1, max: () => 30, warningMin: 2, warningMax: () => 15 },
  REPETITIONS: { min: 0, max: () => 10000, warningMax: () => 1000 },
  SPEED: { min: 0, max: () => 150, warningMax: () => 50 },
  STEPS: { min: 0, max: (h) => Math.max(10000, h * 15000), warningMax: (h) => Math.max(5000, h * 10000) },
  STEPS_PER_MINUTE: { min: 0, max: () => 300, warningMin: 60, warningMax: () => 200 },
  WATTS: { min: 0, max: () => 2000, warningMax: () => 500 },
  WEIGHT: { min: 0, max: () => 500, warningMax: () => 200 },
};

export const AUTO_CALCULATED_KEYS: BaseTrackingKey[] = ['CALORIES', 'PACE', 'SPEED'];

export type ValidationResult = {
  isValid: boolean;
  isWarning: boolean;
  message?: string;
};

export type SuggestedValue = {
  value: number;
  label: string;
};

export type UseTrackingKeyLogicOptions = {
  activityLog: ActivityLog;
  activity: Activity;
  bmr?: number;
  met?: number;
};

export type UseTrackingKeyLogicResult = {
  validateValue: (key: BaseTrackingKey, value: number) => ValidationResult;
  getSuggestedValue: (key: BaseTrackingKey) => SuggestedValue | null;
  isAutoCalculated: (key: BaseTrackingKey) => boolean;
  calculateCalories: (durationSeconds: number, met: number, bmr: number) => number;
  calculatePaceAndSpeed: (distanceKm: number, durationMs: number) => { pace: number; speed: number };
};

const DEFAULT_MET_BY_TAG: Record<string, number> = {
  RUNNING: 8.0,
  WALKING: 3.5,
  CYCLING: 7.0,
  SWIMMING: 6.0,
  YOGA: 2.5,
  STRENGTH: 5.0,
  HIIT: 8.0,
  DEFAULT: 4.0,
};

function getActivityMet(activity: Activity): number {
  const tag = activity.tags[0]?.toUpperCase() || '';
  return DEFAULT_MET_BY_TAG[tag] || DEFAULT_MET_BY_TAG.DEFAULT;
}

function getActivityIntensity(activity: Activity): 'low' | 'medium' | 'high' {
  const tag = activity.tags[0]?.toUpperCase() || '';
  if (['YOGA', 'WALKING', 'STRETCHING'].includes(tag)) return 'low';
  if (['RUNNING', 'HIIT', 'SWIMMING'].includes(tag)) return 'high';
  return 'medium';
}

export function useTrackingKeyLogic({
  activityLog,
  activity,
  bmr,
  met: metOverride,
}: UseTrackingKeyLogicOptions): UseTrackingKeyLogicResult {
  const durationMs = getActivityLogDurationMs(activityLog);
  const durationHours = durationMs / (1000 * 60 * 60) || 1;
  const distance = activityLog.data['DISTANCE'] ?? 0;
  const met = metOverride ?? getActivityMet(activity);

  const calculateCalories = useCallback((durationSeconds: number, activityMet: number, userBmr: number): number => {
    // Standard MET-based calorie calculation
    // Calories = MET * weight(kg) * time(hours)
    // BMR already factors in weight, so: Calories = (MET * BMR / 24) * hours
    const hours = durationSeconds / 3600;
    return Math.round(((activityMet * userBmr) / 24) * hours);
  }, []);

  const calculatePaceAndSpeed = useCallback(
    (distanceKm: number, durationMillis: number): { pace: number; speed: number } => {
      if (distanceKm <= 0 || durationMillis <= 0) {
        return { pace: 0, speed: 0 };
      }
      const durationMinutes = durationMillis / (1000 * 60);
      const durationHrs = durationMillis / (1000 * 60 * 60);
      const pace = durationMinutes / distanceKm; // min/km
      const speed = distanceKm / durationHrs; // km/h
      return { pace: Math.round(pace * 100) / 100, speed: Math.round(speed * 100) / 100 };
    },
    [],
  );

  const validateValue = useCallback(
    (key: BaseTrackingKey, value: number): ValidationResult => {
      const limits = VALIDATION_LIMITS[key];
      if (!limits) {
        return { isValid: true, isWarning: false };
      }

      const max = limits.max?.(durationHours) ?? Number.MAX_SAFE_INTEGER;
      const min = limits.min ?? 0;
      const warningMax = limits.warningMax?.(durationHours);
      const warningMin = limits.warningMin;

      if (value < min) {
        return { isValid: false, isWarning: false, message: `Value must be at least ${min}` };
      }
      if (value > max) {
        return { isValid: false, isWarning: false, message: `Value seems too high (max ~${Math.round(max)})` };
      }
      if (warningMin !== undefined && value < warningMin && value > 0) {
        return { isValid: true, isWarning: true, message: `Value seems low for this activity` };
      }
      if (warningMax !== undefined && value > warningMax) {
        return { isValid: true, isWarning: true, message: `Value seems high - are you sure?` };
      }

      return { isValid: true, isWarning: false };
    },
    [durationHours],
  );

  const getSuggestedValue = useCallback(
    (key: BaseTrackingKey): SuggestedValue | null => {
      const durationSeconds = durationMs / 1000;

      if (key === 'CALORIES' && bmr && met && durationSeconds > 0) {
        const calories = calculateCalories(durationSeconds, met, bmr);
        return { value: calories, label: `~${calories} (estimated)` };
      }

      if ((key === 'PACE' || key === 'SPEED') && distance > 0 && durationMs > 0) {
        const { pace, speed } = calculatePaceAndSpeed(distance, durationMs);
        if (key === 'PACE') {
          return { value: pace, label: `~${pace.toFixed(1)} min/km` };
        }
        if (key === 'SPEED') {
          return { value: speed, label: `~${speed.toFixed(1)} km/h` };
        }
      }

      if (key === 'STEPS' && durationMs > 0) {
        // Rough estimate: 100 steps/min for walking, adjust by activity
        const minutes = durationMs / (1000 * 60);
        const intensity = getActivityIntensity(activity);
        const stepsPerMin = intensity === 'low' ? 80 : intensity === 'high' ? 150 : 110;
        const steps = Math.round(minutes * stepsPerMin);
        return { value: steps, label: `~${steps.toLocaleString()} (estimated)` };
      }

      return null;
    },
    [durationMs, bmr, met, distance, activity, calculateCalories, calculatePaceAndSpeed],
  );

  const isAutoCalculated = useCallback((key: BaseTrackingKey): boolean => {
    return AUTO_CALCULATED_KEYS.includes(key);
  }, []);

  return {
    validateValue,
    getSuggestedValue,
    isAutoCalculated,
    calculateCalories,
    calculatePaceAndSpeed,
  };
}

export function getTrackingKeyIcon(key: BaseTrackingKey): string {
  const icons: Record<string, string> = {
    DURATION: 'bi-clock',
    DISTANCE: 'bi-geo-alt',
    CALORIES: 'bi-fire',
    STEPS: 'bi-footprints',
    HEART_RATE: 'bi-heart-pulse',
    SPEED: 'bi-speedometer2',
    PACE: 'bi-stopwatch',
    REPETITIONS: 'bi-arrow-repeat',
    WEIGHT: 'bi-box',
    LEVEL: 'bi-bar-chart',
    WATTS: 'bi-lightning',
    STEPS_PER_MINUTE: 'bi-activity',
  };
  return icons[key] || 'bi-circle';
}
